import {
  Dispatch,
  FC,
  SetStateAction,
} from "react";
import {
  Box,
  Typography,
  useTheme,
  Theme,
  Radio,
  TextField,
  Slider,
  useMediaQuery,
} from "@mui/material";
import CustomSwitch from "../../../components/CustomSwitch";
import { CampaignPosition } from "../../../shared/types/campaignTypes";
import { makeStyles } from "@mui/styles";
import PositionPreview from "./PositionPreview";
import CustomButton from "../../../components/CustomButton";
import { useDispatch } from "react-redux";
import { CampaignManager } from "../../../services/api/CampaignManager";
import { showFlashMessageWithTimeout } from "../../../services/actions/flashMessage-actions";
import { useParams } from "react-router-dom";
import { CampaignVisualSettingsDto } from "../../../shared/types/campaignTypes";
import StandardSettingsModal from "./StandardSettingsModal";
import NotificationPreview from "../../../components/NotificationPreview";
import { useTranslation } from "react-i18next";
import { useEditCampaign } from "../../../context/EditCampaignContext";
import { BorderType } from "../../../components/NotificationEditComponents/BorderSelect";

interface PcSettingsType {
  isActive: boolean;
  position: CampaignPosition;
  showAfterSeconds?: number;
  showDurationSeconds?: number;
  nextNotificationDelaySeconds?: number;
  animationSpeed?: number;
}

interface PhoneSettingsType {
  isActive: boolean;
  position: CampaignPosition;
  showAfterSeconds?: number;
  showDurationSeconds?: number;
  nextNotificationDelaySeconds?: number;
  animationSpeed?: number;
}

const pcPositionOptions: CampaignPosition[] = [
  "bottomLeft",
  "topLeft",
  "topRight",
  "bottomRight",
];
const phonePositionOptions: CampaignPosition[] = ["bottom", "top"];

interface CampaignViewProps {
  createPcPreview: () => Promise<void>;
  createPhonePreview: () => Promise<void>;
  setIsDefaultUpdateLoading: Dispatch<SetStateAction<boolean>>;
  getLocalSettings: (
    settings: CampaignVisualSettingsDto,
    type: "pc" | "phone",
  ) => PcSettingsType | PhoneSettingsType;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  setPcPreview: Dispatch<SetStateAction<string>>;
  setPhonePreview: Dispatch<SetStateAction<string>>;
  setPcSettings: Dispatch<SetStateAction<PcSettingsType>>;
  setPhoneSettings: Dispatch<SetStateAction<PhoneSettingsType>>;
  pcPreview: string;
  pcSettings: PcSettingsType;
  phoneSettings: PhoneSettingsType;
  phonePreview: string;
  isDefaultUpdateLoading: boolean;
  isModalOpen: boolean;
  isLoading: boolean;
  isFetching: boolean;
  selectedBorder: BorderType | null;
}

const CampaignView: FC<CampaignViewProps> = ({
  setIsDefaultUpdateLoading,
  getLocalSettings,
  setIsModalOpen,
  pcPreview,
  phonePreview,
  isDefaultUpdateLoading,
  isModalOpen,
  selectedBorder,
  pcSettings,
  phoneSettings,
  setPcSettings,
  setPhoneSettings,
  createPcPreview,
  createPhonePreview,
  isFetching,
  isLoading,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery("(min-width:1350px)");

  const classes = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation();
  const { setDetectedChange } = useEditCampaign();

  const toggleSettings = (type: "phone" | "pc") => {
    if (type === "pc") {
      setPcSettings(state => {
        const copy: PcSettingsType = { ...state };
        copy.isActive = !copy.isActive;
        return copy;
      });
    } else {
      setPhoneSettings(state => {
        const copy: PhoneSettingsType = { ...state };
        copy.isActive = !copy.isActive;
        return copy;
      });
    }
    setDetectedChange(true);
  };

  const handlePositionChange = (
    position: CampaignPosition,
    type: "phone" | "pc",
  ) => {
    if (type === "pc") {
      setPcSettings(state => {
        const copy: PcSettingsType = { ...state };
        copy.position = position;
        return copy;
      });
    }
    if (type === "phone") {
      setPhoneSettings(state => {
        const copy: PhoneSettingsType = { ...state };
        copy.position = position;
        return copy;
      });
    }
    setDetectedChange(true);
  };

  const handleShowAfterSecondsChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: "phone" | "pc",
  ) => {
    if (type === "pc") {
      setPcSettings(state => {
        const copy: PcSettingsType = { ...state };
        copy.showAfterSeconds = parseInt(event.target.value, 10);
        return copy;
      });
    }
    if (type === "phone") {
      setPhoneSettings(state => {
        const copy: PhoneSettingsType = { ...state };
        copy.showAfterSeconds = parseInt(event.target.value, 10);
        return copy;
      });
    }
    setDetectedChange(true);
  };

  const handleShowDurationSecondsChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: "phone" | "pc",
  ) => {
    if (type === "pc") {
      setPcSettings(state => {
        const copy: PcSettingsType = { ...state };
        copy.showDurationSeconds = parseInt(event.target.value, 10);
        return copy;
      });
    }
    if (type === "phone") {
      setPhoneSettings(state => {
        const copy: PhoneSettingsType = { ...state };
        copy.showDurationSeconds = parseInt(event.target.value, 10);
        return copy;
      });
    }
    setDetectedChange(true);
  };

  const handleNextNotificationDelaySecondsChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: "phone" | "pc",
  ) => {
    if (type === "pc") {
      setPcSettings(state => {
        const copy: PcSettingsType = { ...state };
        copy.nextNotificationDelaySeconds = parseInt(event.target.value, 10);
        return copy;
      });
    }
    if (type === "phone") {
      setPhoneSettings(state => {
        const copy: PhoneSettingsType = { ...state };
        copy.nextNotificationDelaySeconds = parseInt(event.target.value, 10);
        return copy;
      });
    }
    setDetectedChange(true);
  };

  const handleAnimationSpeedChange = (
    event: Event,
    newValue: number | number[],
    type: "pc" | "phone",
  ) => {
    if (type === "pc") {
      setPcSettings(state => {
        const copy: PcSettingsType = { ...state };
        if (copy.animationSpeed === newValue) {
          createPcPreview();
        }
        copy.animationSpeed = newValue as number;
        return copy;
      });
    }
    if (type === "phone") {
      setPhoneSettings(state => {
        const copy: PhoneSettingsType = { ...state };
        if (copy.animationSpeed === newValue) {
          createPhonePreview();
        }
        copy.animationSpeed = newValue as number;
        return copy;
      });
    }
    setDetectedChange(true);
  };

  const getPositionString = (position: CampaignPosition) => {
    switch (position) {
      case "bottom":
        return "down";
      case "top":
        return "up";
      case "bottomLeft":
        return "downLeftCorner";
      case "bottomRight":
        return "downRightCorner";
      case "topLeft":
        return "upLeftCorner";
      case "topRight":
        return "upRightCorner";
      default:
        return "";
    }
  };

  const onSetDefaultSettings = async () => {
    if (id) {
      try {
        setIsDefaultUpdateLoading(true);
        const _campaignManager = CampaignManager.getManager();
        const result = await _campaignManager.updateCampaignVisualAsync(
          id,
          {
            position: undefined,
            showAfterSeconds: undefined,
            showDurationSeconds: undefined,
            nextNotificationDelaySeconds: undefined,
            animationSpeed: undefined,
          },
          {
            position: undefined,
            showAfterSeconds: undefined,
            showDurationSeconds: undefined,
            nextNotificationDelaySeconds: undefined,
            animationSpeed: undefined,
          },
        );
        const localPcSettings = getLocalSettings(result.visualPcSettings, "pc");
        const localPhoneSettings = getLocalSettings(
          result.visualPhoneSettings,
          "phone",
        );
        setPcSettings(localPcSettings);
        setPhoneSettings(localPhoneSettings);
        setIsDefaultUpdateLoading(false);
        setIsModalOpen(false);
        showFlashMessageWithTimeout(
          dispatch,
          t("popups.visualChanged"),
          "success",
          3000,
        );
      } catch (e: any) {
        setIsDefaultUpdateLoading(false);
        showFlashMessageWithTimeout(dispatch, e.toString(), "error");
      }
    }
  };

  const sliderStyles = (disabled: boolean) => {
    return {
      color: "#FFCF96",
      "& .MuiSlider-track": {
        border: "none",
        color: disabled ? theme.palette.natural.t3 : undefined,
      },
      "& .MuiSlider-thumb": {
        width: 20,
        height: 20,
        borderRadius: 0,
        background: disabled
          ? theme.palette.natural.t3
          : theme.palette.main.gradient.primary.p2,
        "&:before": {
          boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
        },
        "&:hover, &.Mui-focusVisible, &.Mui-active": {
          boxShadow: "none",
        },
      },
    };
  };

  return (
    <Box style={{ flex: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "32px",
        }}
      >
        {/* PC part */}

        <Box
          sx={{
            width: "500px",
            marginRight: "16px",
          }}
        >
          <Box className={classes.row}>
            <Typography className={classes.rowTitle}>
              {t("campaign.design.computer")}
            </Typography>
            <CustomSwitch
              checked={pcSettings.isActive}
              onSwitch={() => toggleSettings("pc")}
            />
          </Box>

          {pcSettings.isActive ? (
            <>
              <Typography
                className={classes.positionTitle}
                style={{ marginTop: 24 }}
              >
                {t("campaign.design.position")}
              </Typography>

              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      maxWidth: "400px",
                      minHeight: "300px",
                    }}
                  >
                    {pcPositionOptions.map((option, index) => (
                      <Box key={index} className={classes.listItem}>
                        <Box
                          style={{ marginLeft: 13 }}
                          onClick={() => handlePositionChange(option, "pc")}
                        >
                          <PositionPreview
                            key={`${option}-${index}`}
                            isActive={
                              !pcSettings.isActive
                                ? false
                                : pcSettings.position === option
                            }
                            position={option}
                            index={index}
                          />
                        </Box>
                        <Box className={classes.radioRow}>
                          <Radio
                            sx={{
                              color: theme.palette.natural.t3,
                              "&.Mui-checked": {
                                color: pcSettings.isActive
                                  ? theme.palette.main.primary.o
                                  : theme.palette.natural.t3,
                              },
                            }}
                            checked={pcSettings.position === option}
                            onChange={() => handlePositionChange(option, "pc")}
                            value={option}
                            disabled={!pcSettings.isActive}
                          />
                          <Typography className={classes.radioLabel}>
                            {t("campaign.design." + getPositionString(option))}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Typography
                    className={classes.positionTitle}
                    style={{ marginTop: 40 }}
                  >
                    {t("campaign.design.animations")}
                  </Typography>
                  <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.showAfter")}
                      </Typography>
                      <TextField
                        disabled={!pcSettings.isActive}
                        style={{ width: 108 }}
                        InputProps={{
                          inputProps: { min: 0 },
                          style: { height: 44 },
                        }}
                        type='number'
                        variant='outlined'
                        value={pcSettings.showAfterSeconds}
                        onChange={event =>
                          handleShowAfterSecondsChange(event, "pc")
                        }
                      />
                      <Typography className={classes.secondaryText}>
                        {t("campaign.design.animationSettings.seconds")}
                      </Typography>
                    </Box>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.showDelay")}
                      </Typography>
                      <TextField
                        disabled={!pcSettings.isActive}
                        style={{ width: 108 }}
                        InputProps={{
                          inputProps: { min: 0 },
                          style: { height: 44 },
                        }}
                        type='number'
                        variant='outlined'
                        value={pcSettings.showDurationSeconds}
                        onChange={event =>
                          handleShowDurationSecondsChange(event, "pc")
                        }
                      />
                      <Typography className={classes.secondaryText}>
                        {t("campaign.design.animationSettings.second")}
                      </Typography>
                    </Box>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.delayBetween")}
                      </Typography>
                      <TextField
                        disabled={!pcSettings.isActive}
                        style={{ width: 108 }}
                        InputProps={{
                          inputProps: { min: 0 },
                          style: { height: 44 },
                        }}
                        type='number'
                        variant='outlined'
                        value={pcSettings.nextNotificationDelaySeconds}
                        onChange={event =>
                          handleNextNotificationDelaySecondsChange(event, "pc")
                        }
                      />
                      <Typography className={classes.secondaryText}>
                        {t("campaign.design.animationSettings.second")}
                      </Typography>
                    </Box>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.showSpeed")}
                      </Typography>
                      <Box style={{ width: 200 }}>
                        <Slider
                          disabled={!pcSettings.isActive}
                          aria-label='Volume'
                          value={pcSettings.animationSpeed}
                          onChange={(event, newValue) =>
                            handleAnimationSpeedChange(event, newValue, "pc")
                          }
                          sx={sliderStyles(!pcSettings.isActive)}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Box className={classes.previewContainer}>
                    <Box style={{ height: 20, width: "100%" }} />
                    <NotificationPreview
                      backgroundColor='transparent'
                      notificationHtml={pcPreview}
                    />
                  </Box>
                  <Typography
                    className={classes.replayAnimationText}
                    onClick={() => createPcPreview()}
                  >
                    {t("campaign.design.playAnimation")}
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <Typography fontStyle='italic' sx={{ padding: "16px 0" }}>
              {t("campaign.design.computerOff")}
            </Typography>
          )}
        </Box>

        {isDesktop && (
          <Box
            sx={{
              width: "2px",
              backgroundColor: theme.palette.natural.t8,
            }}
          />
        )}

        {/* PHONE part */}

        <Box
          sx={{
            marginLeft: "16px",
          }}
        >
          <Box className={classes.row}>
            <Typography className={classes.rowTitle}>
              {t("campaign.design.mobile")}
            </Typography>
            <CustomSwitch
              checked={phoneSettings.isActive}
              onSwitch={() => toggleSettings("phone")}
            />
          </Box>
          {phoneSettings.isActive ? (
            <>
              <Typography
                className={classes.positionTitle}
                style={{ marginTop: 24 }}
              >
                {t("campaign.design.position")}
              </Typography>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    maxWidth: 800,
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      minHeight: "300px",
                    }}
                  >
                    <Box className={classes.pcPositionList}>
                      {phonePositionOptions.map((option, index) => (
                        <Box
                          key={index}
                          className={classes.listItem}
                          style={{
                            marginLeft: index === 0 ? 0 : 38,
                            width: 79,
                          }}
                        >
                          <Box
                            style={{ marginLeft: 13 }}
                            onClick={() =>
                              handlePositionChange(option, "phone")
                            }
                          >
                            <PositionPreview
                              key={`${option}-${index}`}
                              isActive={
                                !phoneSettings.isActive
                                  ? false
                                  : phoneSettings.position === option
                              }
                              position={option}
                              index={index}
                            />
                          </Box>
                          <Box className={classes.radioRow}>
                            <Radio
                              sx={{
                                color: theme.palette.natural.t3,
                                "&.Mui-checked": {
                                  color: phoneSettings.isActive
                                    ? theme.palette.main.primary.o
                                    : theme.palette.natural.t3,
                                },
                              }}
                              checked={phoneSettings.position === option}
                              onChange={() =>
                                handlePositionChange(option, "phone")
                              }
                              value={option}
                              disabled={!phoneSettings.isActive}
                            />
                            <Typography className={classes.radioLabel}>
                              {t(
                                "campaign.design." + getPositionString(option),
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Typography
                    className={classes.positionTitle}
                    style={{ marginTop: 40 }}
                  >
                    {t("campaign.design.animations")}
                  </Typography>
                  <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.showAfter")}
                      </Typography>
                      <TextField
                        style={{ width: 108 }}
                        InputProps={{
                          inputProps: { min: 0 },
                          style: { height: 44 },
                        }}
                        type='number'
                        variant='outlined'
                        value={phoneSettings.showAfterSeconds}
                        onChange={event =>
                          handleShowAfterSecondsChange(event, "phone")
                        }
                        disabled={!phoneSettings.isActive}
                      />
                      <Typography className={classes.secondaryText}>
                        {t("campaign.design.animationSettings.seconds")}
                      </Typography>
                    </Box>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.showDelay")}
                      </Typography>
                      <TextField
                        style={{ width: 108 }}
                        InputProps={{
                          inputProps: { min: 0 },
                          style: { height: 44 },
                        }}
                        type='number'
                        variant='outlined'
                        value={phoneSettings.showDurationSeconds}
                        onChange={event =>
                          handleShowDurationSecondsChange(event, "phone")
                        }
                        disabled={!phoneSettings.isActive}
                      />
                      <Typography className={classes.secondaryText}>
                        {t("campaign.design.animationSettings.second")}
                      </Typography>
                    </Box>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.delayBetween")}
                      </Typography>
                      <TextField
                        style={{ width: 108 }}
                        InputProps={{
                          inputProps: { min: 0 },
                          style: { height: 44 },
                        }}
                        type='number'
                        variant='outlined'
                        value={phoneSettings.nextNotificationDelaySeconds}
                        onChange={event =>
                          handleNextNotificationDelaySecondsChange(
                            event,
                            "phone",
                          )
                        }
                        disabled={!phoneSettings.isActive}
                      />
                      <Typography className={classes.secondaryText}>
                        {t("campaign.design.animationSettings.second")}
                      </Typography>
                    </Box>
                    <Box className={classes.paramRow}>
                      <Typography className={classes.primaryText}>
                        {t("campaign.design.animationSettings.showSpeed")}
                      </Typography>
                      <Box style={{ width: 200 }}>
                        <Slider
                          aria-label='Volume'
                          value={phoneSettings.animationSpeed}
                          onChange={(event, newValue) => {
                            handleAnimationSpeedChange(
                              event,
                              newValue,
                              "phone",
                            );
                          }}
                          sx={sliderStyles(!phoneSettings.isActive)}
                          disabled={!phoneSettings.isActive}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Box className={classes.previewContainer}>
                    <Box style={{ height: 20, width: "100%" }} />
                    <NotificationPreview
                      backgroundColor='transparent'
                      notificationHtml={phonePreview}
                    />
                  </Box>
                  <Typography
                    className={classes.replayAnimationText}
                    onClick={() => createPhonePreview()}
                  >
                    {t("campaign.design.playAnimation")}
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <Typography fontStyle='italic' sx={{ padding: "16px 0" }}>
              {t("campaign.design.mobileOff")}
            </Typography>
          )}
        </Box>
      </Box>

      <Box className={classes.divider} />
      <Box className={classes.buttonsContainer}>
        <CustomButton
          size='medium'
          style={{ width: 186, marginRight: 24 }}
          type='secondary'
          title={t("defaultSettings")}
          onClick={() => setIsModalOpen(true)}
        />
      </Box>
      <StandardSettingsModal
        isLoading={isDefaultUpdateLoading}
        isOpen={isModalOpen}
        onModalClose={() => setIsModalOpen(false)}
        onMainButtonPress={onSetDefaultSettings}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  row: {
    flex: 1,
    height: 44,
    width: "100%",
    marginRight: 24,
    marginTop: 24,
    backgroundColor: theme.palette.natural.t6,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  rowTitle: {
    fontSize: 22,
    color: theme.palette.natural.t,
    fontWeight: "bold",
    marginRight: 24,
    marginLeft: 14,
  },
  positionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: theme.palette.natural.t,
    marginBottom: 16,
  },
  pcPositionList: {
    display: "flex",
    flexDirection: "row",
    maxWidth: 782,
  },
  listItem: {
    width: 200,
    display: "flex",
    flexDirection: "column",
  },
  radioRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  radioLabel: {
    userSelect: "none",
    fontSize: 16,
    color: theme.palette.natural.t1,
  },
  paramRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 24,
  },
  secondaryText: {
    marginLeft: 16,
    fontSize: 16,
    color: theme.palette.natural.t1,
  },
  primaryText: {
    width: 300,
    fontSize: 16,
    color: theme.palette.natural.t1,
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row-reverse",
    flex: 1,
    height: 44,
    marginBottom: 32,
    marginRight: "10%",
  },
  divider: {
    height: 2,
    flex: 1,
    backgroundColor: theme.palette.natural.t8,
    marginBottom: 32,
  },
  previewContainer: {
    height: 300,
    minWidth: 300,
    backgroundColor: theme.palette.main.primary.d3,
    display: "flex",
    paddingLeft: 12,
    paddingRight: 12,
    flexDirection: "column",
  },
  replayAnimationText: {
    fontSize: 16,
    color: theme.palette.main.primary.o,
    marginTop: 20,
    cursor: "pointer",
  },
}));

export default CampaignView;
